import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiCheckCircle, FiEye, FiFileText } from "react-icons/fi";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  ActionPill,
  CardBox,
  CardHeaderIcon,
  ClampDescription,
} from "./trainingUi";
import {
  getTrainingLessonsApi,
  getTrainingLessonViewCountsApi,
} from "../../api/training.api";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#18212F";
const MUTED = "#94A3B8";
const TERTIARY = "#A4AFC1";

const LessonCard = ({ item, viewCount = 0, onClick }) => (
  <CardBox onClick={onClick}>
    <Flex align="center" gap="8px">
      <CardHeaderIcon icon={<FiFileText />} />
      <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">
        {item.title}
      </Text>
    </Flex>
    <Box h="10px" />
    <ClampDescription html={item.description} />
    <Box h="12px" />
    <Flex align="center" justify="space-between">
      <Flex align="center" gap="8px">
        <Flex
          align="center"
          gap="4px"
          color={TERTIARY}
          fontSize="12px"
          fontWeight={600}
        >
          <FiEye size={14} />
          {viewCount} lượt xem
        </Flex>
        {item.completed && (
          <Flex
            align="center"
            gap="6px"
            bg="#E9FFF4"
            color="#0BA859"
            borderRadius="6px"
            px="10px"
            py="4px"
            fontSize="12px"
            fontWeight={600}
          >
            <FiCheckCircle size={14} />
            Đã hoàn thành
          </Flex>
        )}
      </Flex>
      <ActionPill label="Học ngay" icon={null} />
    </Flex>
  </CardBox>
);

const LessonList = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { lectureId } = useParams();
  const location = useLocation();
  const topicName = location.state?.topicName || "";
  const courseNameFromState = location.state?.courseName || "";

  const [items, setItems] = useState([]);
  const [lectureTitle, setLectureTitle] = useState("");
  const [viewCounts, setViewCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!lectureId) return;
    setLoading(true);
    try {
      const data = await getTrainingLessonsApi(lectureId);
      setItems(data?.items || []);
      setLectureTitle(data?.lectureTitle || "");
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
    // Lượt xem chỉ để hiển thị, lỗi ở đây không ảnh hưởng danh sách bài học.
    try {
      const vc = await getTrainingLessonViewCountsApi(lectureId);
      const map = {};
      (vc?.items || []).forEach((r) => {
        map[r.lessonId] = r.viewCount;
      });
      setViewCounts(map);
    } catch {
      setViewCounts({});
    }
  }, [lectureId]);

  useEffect(() => {
    load();
  }, [load]);

  const courseName = courseNameFromState || lectureTitle;

  const pageBody = (
    <>

      <Box px="16px" pt="12px">
        {loading && !items.length ? (
          <Flex justify="center" py="40px">
            <Spinner color={APP.primary200} />
          </Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
            Không có bài học
          </Text>
        ) : (
          <Flex direction="column" gap="14px">
            {items.map((it) => (
              <LessonCard
                key={it._id}
                item={it}
                viewCount={viewCounts[it._id] ?? 0}
                onClick={() =>
                  navigate(`${EMP_BASE_PATH}/dao-tao/bai-hoc/${it._id}`, {
                    state: { topicName, courseName },
                  })
                }
              />
            ))}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={lectureTitle || "Bài học"} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={lectureTitle || "Bài học"} />
      {pageBody}
    </Box>
  );
};

export default LessonList;
